import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { useFormField } from "../../hooks/useFormField";
import { useIsMobile } from "../../hooks/useIsMobile";
import { FieldHint } from "../FieldHint";
import { labelClass, resolveFieldState, type FieldState } from "./fieldStyles";
import type { IntakeFormData, Mismatch } from "../../types";
import type { useStepValidation } from "../../hooks/useStepValidation";

type StringField = {
  [K in keyof IntakeFormData]: IntakeFormData[K] extends string ? K : never;
}[keyof IntakeFormData];

/**
 * Option entry. Pass either simple strings or { value, label } pairs.
 * Empty-string values become the "Select..." placeholder.
 */
export type SelectOption = string | { value: string; label: string };

function normalize(opt: SelectOption): { value: string; label: string } {
  return typeof opt === "string" ? { value: opt, label: opt } : opt;
}

/** Props when backed by the form store (existing usage). */
interface FormStoreProps {
  field: StringField;
  validation: ReturnType<typeof useStepValidation>;
  value?: never;
  error?: never;
}

/** Props when controlled externally (e.g. employee modal, page size). */
interface ControlledProps {
  field?: never;
  validation?: never;
  value: string;
  error?: string;
}

type SelectInputProps = (FormStoreProps | ControlledProps) & {
  label: string;
  options: readonly SelectOption[];
  required?: boolean;
  /** Label shown for the empty-string option. Default: "Select..." */
  placeholder?: string;
  /** Static helper text rendered between the label and the input. */
  hint?: string;
  containerClassName?: string;
  /** Fires after the value changes, with the new value. */
  onChange?: (value: string) => void;
  /** Hide the check icon on the selected option. Default: false */
  hideCheck?: boolean;
  /** Size variant. "default" for form fields, "sm" for compact inline use. */
  size?: "default" | "sm";
  /** Hide the empty placeholder option in the dropdown. Use when a value is always set. */
  hidePlaceholder?: boolean;
};

/**
 * Styled dropdown using Headless UI Listbox — rounded dropdown panel,
 * check icon on the left of the selected option, keyboard accessible.
 *
 * Two modes:
 *   // Form store mode (reads/writes useFormField)
 *   <SelectInput field="state" label="State" validation={validation} required
 *     options={[{ value: "NY", label: "New York" }, ...]} />
 *
 *   // Controlled mode (caller manages value)
 *   <SelectInput label="Coverage" value={val} onChange={setVal}
 *     options={["Enrolling", "Waiving"]} error={err} />
 */
/** Props after mode-dispatch — value/error/etc resolved by the wrapper. */
interface SelectInputViewProps {
  label: string;
  options: readonly SelectOption[];
  required?: boolean;
  placeholder?: string;
  hint?: string;
  containerClassName?: string;
  onChange?: (value: string) => void;
  hideCheck?: boolean;
  size?: "default" | "sm";
  hidePlaceholder?: boolean;
  fieldId: string;
  currentValue: string;
  currentError: string | null;
  isPrefilled: boolean;
  mismatch: Mismatch | undefined;
  onValueChange: (val: string) => void;
}

export function SelectInput(props: SelectInputProps) {
  // Dispatch on mode so useFormField is only called when actually backed by the
  // form store — avoids subscribing to an unrelated sentinel field in controlled mode.
  if (props.field) {
    return <FormSelectInput {...props} />;
  }
  return <ControlledSelectInput {...props} />;
}

function FormSelectInput(props: FormStoreProps & Omit<SelectInputProps, keyof FormStoreProps>) {
  const formField = useFormField(props.field, props.validation);
  const fieldId = props.field;
  return (
    <SelectInputView
      {...stripModeProps(props)}
      fieldId={fieldId}
      currentValue={formField.value as string}
      currentError={formField.error}
      isPrefilled={formField.isPrefilled}
      mismatch={formField.mismatch}
      onValueChange={(val) => {
        formField.setValue(val as never);
        formField.onBlur();
        props.onChange?.(val);
      }}
    />
  );
}

function ControlledSelectInput(props: ControlledProps & Omit<SelectInputProps, keyof ControlledProps>) {
  const fieldId = props.label.toLowerCase().replace(/\s+/g, "-");
  return (
    <SelectInputView
      {...stripModeProps(props)}
      fieldId={fieldId}
      currentValue={props.value}
      currentError={props.error ?? null}
      isPrefilled={false}
      mismatch={undefined}
      onValueChange={(val) => props.onChange?.(val)}
    />
  );
}

/** Drop the mode-discriminator keys before forwarding to the view. */
function stripModeProps(props: SelectInputProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { field, validation, value, error, ...rest } = props as Record<string, unknown> & SelectInputProps;
  return rest as Omit<SelectInputViewProps, "fieldId" | "currentValue" | "currentError" | "isPrefilled" | "mismatch" | "onValueChange">;
}

function SelectInputView({
  label,
  options,
  required,
  placeholder = "Select...",
  hint,
  containerClassName,
  hideCheck,
  size = "default",
  hidePlaceholder,
  fieldId,
  currentValue,
  currentError,
  isPrefilled,
  mismatch,
  onValueChange,
}: SelectInputViewProps) {
  const isSmall = size === "sm";

  const handleChange = (val: string) => {
    onValueChange(val);
  };

  const state = resolveFieldState({
    error: currentError,
    isMismatch: !!mismatch,
    isPrefilled,
  });

  const normalized = options.map(normalize);
  const selected = normalized.find((o) => o.value === currentValue);
  const displayLabel = selected && selected.value !== "" ? selected.label : placeholder;
  const isPlaceholder = !selected || selected.value === "";
  const isMobile = useIsMobile();
  const errorId = currentError ? `${fieldId}-err` : undefined;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  const stateClasses: Record<FieldState, string> = {
    default: "border-gray-300 bg-white",
    prefilled: "border-success bg-success-bg",
    mismatch: "border-warning bg-warning-bg",
    error: "border-danger bg-danger-bg",
    readonly: "border-gray-200 bg-gray-50 text-gray-700",
  };

  const buttonClass = `${isSmall ? "" : "w-full"} rounded-lg border ${isSmall ? "pl-3 pr-7 py-1.5 text-[13px]" : "pl-3 pr-8 py-2.5 text-sm"} text-left cursor-pointer
    focus:outline-none focus:ring-2 focus:ring-brand-green-50 focus:border-brand-green
    ${stateClasses[state]} ${isPlaceholder ? "text-gray-400" : "text-gray-900"}`;

  return (
    <div className={containerClassName}>
      <label htmlFor={fieldId} className={labelClass()}>
        {label}
        {required && <span className="text-danger" aria-hidden="true">&nbsp;*</span>}
      </label>
      {hint && <p id={hintId} className="text-xs text-gray-500 mb-1.5">{hint}</p>}

      {isMobile ? (
        <div className="relative focus-within:ring-2 focus-within:ring-brand-green-50 rounded-lg">
          {/* Styled display layer */}
          <div
            aria-hidden="true"
            className={`${isSmall ? "" : "w-full"} rounded-lg border ${isPlaceholder || hideCheck ? "pl-3" : "pl-10"} ${isSmall ? "pr-7 py-1.5 text-[13px]" : "pr-8 py-2.5 text-base sm:text-sm"} text-left cursor-pointer
              focus:outline-none focus:ring-2 focus:ring-brand-green-50 focus:border-brand-green
              ${stateClasses[state]} ${isPlaceholder ? "text-gray-400" : "text-gray-900 font-medium"}`}
          >
            {!isPlaceholder && !hideCheck && (
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-brand-green animate-fade-in">
                <Check size={16} />
              </span>
            )}
            <span className="block truncate">{displayLabel}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ChevronDown size={16} className="text-gray-400" />
            </span>
          </div>
          {/* Invisible native select triggers OS picker */}
          <select
            id={fieldId}
            aria-label={label}
            aria-invalid={!!currentError}
            aria-required={required || undefined}
            aria-describedby={describedBy}
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer text-base"
          >
            {!hidePlaceholder && <option value="">{placeholder}</option>}
            {normalized.filter((o) => o.value !== "").map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      ) : (
        <Listbox
          value={currentValue}
          onChange={(val: string) => handleChange(val)}
        >
          <div className="relative">
            <ListboxButton
              id={fieldId}
              aria-invalid={!!currentError}
              aria-required={required || undefined}
              aria-describedby={describedBy}
              className={buttonClass}
            >
              <span className="block truncate">{displayLabel}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronDown size={16} className="text-gray-400" />
              </span>
            </ListboxButton>

            <ListboxOptions
              anchor="bottom start"
              modal={false}
              className="z-50 max-h-60 w-[var(--button-width)] overflow-auto rounded-lg bg-white py-1 text-sm
                shadow-lg ring-1 ring-black/5 focus:outline-none [--anchor-gap:4px] [--anchor-max-height:50vh]"
              transition
            >
              {(hidePlaceholder ? normalized.filter((o) => o.value !== "") : normalized).map((opt) => (
                <ListboxOption
                  key={opt.value}
                  value={opt.value}
                  className={`group relative cursor-pointer select-none py-2.5 ${hideCheck ? "pl-4" : "pl-10"} pr-4
                    text-gray-900 data-[focus]:bg-brand-green-10 data-[focus]:text-brand-green`}
                >
                  {({ selected: isSelected }) => (
                    <>
                      {isSelected && !hideCheck && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-brand-green">
                          <Check size={16} />
                        </span>
                      )}
                      <span className={`block truncate ${isSelected ? "font-semibold" : "font-normal"}`}>
                        {opt.value === "" ? placeholder : opt.label}
                      </span>
                    </>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
      )}
      <FieldHint id={errorId} error={currentError} isPrefilled={isPrefilled} mismatch={mismatch} />
    </div>
  );
}
